import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useApproveStocktake } from '@/hooks/use-stocktake-query'
import { m } from '@/lib/paraglide/messages'

interface Props {
    open: boolean
    onOpenChange: (v: boolean) => void
    sessionId: string
    variance: number
    sessionCode: string
}

/**
 * StocktakeApproveDialog — duyệt phiếu chỉ cần confirm.
 * Notes ở cấp phiếu đã bỏ (BE autofill "Từ kiểm kê phiên <code>"); mỗi dòng
 * có variance đã có `line_notes` riêng do user điền lúc kiểm.
 */
export function StocktakeApproveDialog({
    open,
    onOpenChange,
    sessionId,
    variance,
    sessionCode,
}: Props) {
    const workflow = useWorkflowTracking('stocktake_approve', {
        operation: 'approve',
        active: open,
        attemptKey: sessionId,
    })
    const approve = useApproveStocktake()

    const handleApprove = async () => {
        workflow.interact('review')
        try {
            await workflow.run(
                () => approve.mutateAsync(sessionId),
                (result) => result.data?.session.id,
            )
            onOpenChange(false)
        } catch {
            // The mutation hook owns the API error toast.
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {sessionCode} — {m.stocktake_approve_dialog_title()}
                    </DialogTitle>
                    <DialogDescription>
                        {m.stocktake_approve_dialog_variance({
                            variance: String(variance),
                        })}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={approve.isPending}
                    >
                        {m.common_cancel()}
                    </Button>
                    <Button
                        onClick={handleApprove}
                        disabled={approve.isPending}
                    >
                        {m.stocktake_approve_dialog_confirm()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
